import React, { useContext, useEffect, useState, useRef } from 'react'
import { AppContext } from '../../context/app'
import useAxios from 'axios-hooks'
import {
  IPTV_CHANNEL_FILTER,
  IPTV_CHANNEL_EPG_SCHEDULE,
  SYSTEM_METADATA,
} from '../../utils/apiUrls'
import { useNavigate, useParams } from 'react-router'
import { isEmpty } from 'lodash'
import { convertEpgToLocalTime, getImage, xmlParser, cleanEpgPrograms } from '../../utils/helper'
import CustomImage from '../../components/customImage/customImage'
import { LG_SIZE, XL_SIZE } from '../../utils/constants'
import moment from 'moment'
import { useInterval } from '../../hooks/useInterval'

const IptvChannelEPG = () => {
  const navigate = useNavigate()
  const { accountObj } = useContext(AppContext)
  const [allChannelList, setAllChannelList] = useState([])
  const [allCategories, setAllCategories] = useState([])
  const channelId = useParams().channelId
  const [timeConfig, setTimeConfig] = useState('sm')
  const [selectedCategory, setSelectedCategory] = useState({
    name: 'all',
    id: 'all',
  })
  const [channelEpgData, setChannelEpgData] = useState([])
  const [channelSliderHeight, setChannelSliderHeight] = useState(0)
  const [timerLeftMargin, setTimerLeftMargin] = useState(0)
  const [epgLoader, setEpgLoader] = useState(false)

  const channelRef = useRef(null)
  const epgRef = useRef(null)
  const timeSlotRef = useRef(null)
  const channelSliderHeightRef = useRef(null)
  // State to store the height

  const timeStyleConfig = {
    xxl: {
      width: '259px',
      fontSize: '14px',
      timeFont: '13px',
      pixelPerMinute: 8.6333,
    },
    xl: { width: '207px', fontSize: '11px', timeFont: '10px', pixelPerMinute: 6.9 },
    sm: { width: '86px', fontSize: '10px', timeFont: '9px', pixelPerMinute: 2.8666 },
  }

  const handleResize = () => {
    if (window.innerWidth >= XL_SIZE) {
      setTimeConfig('xxl')
    } else if (window.innerWidth < XL_SIZE && window.innerWidth >= LG_SIZE) {
      setTimeConfig('xl')
    } else {
      setTimeConfig('sm')
    }
  }

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const [{ data: categoryRes, loading: categoryLoading }, categoryCall] = useAxios(
    {
      url: `${SYSTEM_METADATA}`,
      method: 'get',
    },
    { manual: true },
  )

  const [{ data: channelData, loading: channelLoading }, channelCall] = useAxios(
    {
      url: IPTV_CHANNEL_FILTER,
      method: 'post',
    },
    {
      manual: true,
    },
  )

  const [{ data: channelSchedule, loading: channelScheduleLoading }, channelScheduleCall] =
    useAxios(
      {
        url: IPTV_CHANNEL_EPG_SCHEDULE,
        method: 'post',
      },
      {
        manual: true,
      },
    )

  useEffect(() => {
    categoryCall()
  }, [])

  useEffect(() => {
    setSelectedCategory({
      name: 'all',
      id: 'all',
    })
    setAllChannelList([])
    channelCall({
      data: {
        filters: {},
        order_by: 'name',
        reverse: true,
        page_size: '1000',
        page: '1',
      },
    })
  }, [channelId])

  useEffect(async () => {
    if (!isEmpty(allChannelList)) {
      const channelIds = allChannelList.map((channel) => channel._id)

      await channelScheduleCall({
        data: { channel_id: channelIds },
        params: {
          start_time: moment().subtract(30, 'minutes').toISOString(),
          end_time: moment().add(48, 'hours').toISOString(),
        },
      })
    }
  }, [allChannelList])

  useEffect(() => {
    if (channelData && channelData.success) {
      channelData.data.sort((a, b) => (a._id === channelId ? -1 : b._id === channelId ? 1 : 0))
      setAllChannelList(channelData.data)
    }
  }, [channelData])

  useEffect(() => {
    if (categoryRes && categoryRes.success) {
      setAllCategories(categoryRes.data.filter((item) => item.type === 'category') || [])
    }
  }, [categoryRes])

  useEffect(() => {
    if (channelSchedule && channelSchedule.success) {
      channelSchedule.data.sort((a, b) =>
        a.channel_id === channelId ? -1 : b.channel_id === channelId ? 1 : 0,
      )
      setChannelEpgData(channelSchedule.data)
      handleExternalChannelEpg(channelSchedule.data)
    }
  }, [channelSchedule])

  const handleExternalChannelEpg = async (epgData) => {
    const externalChannels = allChannelList.filter(
      (channel) => channel.type === 'EXTERNAL' && channel?.epg_url,
    )
    if (!isEmpty(epgData) && !isEmpty(externalChannels)) {
      let externalEpg = []
      for (let i = 0; i < externalChannels.length; i++) {
        const element = externalChannels[i]

        const epg = await xmlParser(element.epg_url)

        if (!isEmpty(epg?.programs)) {
          let obj = {
            channel_id: element._id,
            schedule_data: cleanEpgPrograms(epg.programs),
          }
          externalEpg.push(obj)
        }
      }
      if (!isEmpty(externalEpg)) {
        setChannelEpgData((prev) =>
          prev.map((item) => {
            const externalData = externalEpg.find((ext) => ext.channel_id === item.channel_id)
            return externalData ? { ...item, schedule_data: externalData.schedule_data } : item
          }),
        )
      }
    }
  }

  useEffect(() => {
    setEpgLoader(channelLoading || categoryLoading || channelScheduleLoading)
  }, [channelLoading, categoryLoading, channelScheduleLoading])

  const generateSlots = () => {
    const slots = []
    let startTime = new Date()

    // 1. Round to the next clean 30-minute block
    if (startTime.getMinutes() < 59) {
      if (startTime.getMinutes() < 30) {
        startTime.setMinutes(0, 0, 0)
      } else {
        startTime.setMinutes(30, 0, 0)
      }
    } else {
      startTime.setHours(startTime.getHours() + 1, 0, 0, 0)
    }

    for (let i = 0; i < 96; i++) {
      // Create slot by adding 30 mins * i
      const slotTime = new Date(startTime.getTime() + i * 30 * 60000)

      const timeString = slotTime.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        // hour12: true,
        hourCycle: 'h12',
      })

      const dateString = slotTime.toLocaleDateString([], {
        day: 'numeric',
        month: 'short',
        weekday: 'short',
      })
      // debugger

      slots.push({ time: timeString, date: dateString })
    }
    console.log('slots', slots.length)

    return slots
  }

  // Syncs the "target" div to the "source" div's scroll position
  const SyncScroll = (source, target, isLeftToRight = false) => {
    if (source.current && target.current) {
      if (isLeftToRight) {
        target.current.scrollLeft = source.current.scrollLeft
        return
      }
      target.current.scrollTop = source.current.scrollTop
    }
  }

  let visibleChannels = allChannelList.filter(
    (channel) =>
      selectedCategory.id === 'all' ||
      [...channel.category].find((cat) => cat._id === selectedCategory.id),
  )

  useEffect(() => {
    if (channelSliderHeightRef.current) {
      // Get the height (offsetHeight includes padding and borders)
      const measuredHeight = channelSliderHeightRef.current.offsetHeight
      // Set the height in the state
      setChannelSliderHeight(measuredHeight)
    }
  }, [visibleChannels])

  const getContentTileColor = (info) => {
    const isCurrentPlayingContent =
      moment(new Date()).isSameOrAfter(new Date(info.start_time + 'Z')) &&
      moment(new Date()).isSameOrBefore(new Date(info.end_time + 'Z'))

    if (isCurrentPlayingContent) {
      return {
        bgColor: info.iptv_content_type === 'LIVE' ? '#5C1921' : '#141446',
        borderColor: info.iptv_content_type === 'LIVE' ? '#FF3131' : '#1F51FF',
      }
    }

    return {
      bgColor: info.iptv_content_type === 'LIVE' ? '#5C3819' : '#11171F',
      borderColor: info.iptv_content_type === 'LIVE' ? '#FF9300' : '#727272',
    }
  }

  useInterval(
    async () => {
      const currentTime = new Date()
      const now = new Date()

      const minutes = now.getMinutes()

      // Check if time is between 5:00 and 5:30 (inclusive)
      if (minutes > 0 && minutes <= 30) {
        now.setMinutes(0, 0, 0, 0) // Sets time to 06:00:00.000
      }
      // Check if time is between 5:31 and 6:00 (inclusive)
      else if (minutes > 30 || minutes === 0) {
        now.setMinutes(30, 0, 0, 0)
      }

      const minutesFromNowToStart = Math.floor((currentTime.getTime() - now.getTime()) / 60000)

      setTimerLeftMargin(minutesFromNowToStart * timeStyleConfig[timeConfig].pixelPerMinute)
    },
    timerLeftMargin === 0 ? 1000 : 30000,
  )

  return (
    <div
      className={` w-full h-[260px] xl:h-[295px] 2xl:h-[371px] flex  ${
        epgLoader ? 'animate-pulse' : ''
      }`}
    >
      <div className="mr-[3px] hidden xl:block xl:w-[99px] 2xl:w-[125px]">
        <div
          style={{ background: '#171D24', color: '#CECECE' }}
          className="w-full h-[31px] xl:h-[33px] 2xl:h-[42px] flex items-center justify-start  px-2.5 2xl:px-3 text-[11px] 2xl:text-xs mb-[6px] xl:mb-[9px] 2xl:mb-[11px]"
        >
          Categories
        </div>

        <div style={{}} className="tv-epg-scrollbar-class h-[226px] xl:h-[255px] 2xl:h-[320px]">
          <div
            style={{
              background: selectedCategory.id === 'all' ? '#263241' : '#171D24',
              border: selectedCategory.id === 'all' ? '1px solid #707070' : 'none',
              color: '#FFFFFF',
            }}
            className="w-full xl:h-[40px] 2xl:h-[50px] flex items-center justify-start text-xs 2xl:text-sm mb-1 cursor-pointer rounded xl:rounded-md  px-2.5 2xl:px-3 "
            onClick={() =>
              setSelectedCategory({
                id: 'all',
                name: 'all',
              })
            }
          >
            All Channels
          </div>
          {/* xl:h-[212px] 2xl:h-[266px] */}
          {!isEmpty(allCategories) &&
            allCategories.map((item, index) => (
              <div
                style={{
                  background: selectedCategory.id === item._id ? '#263241' : '#171D24',
                  border: selectedCategory.id === item._id ? '1px solid #707070' : 'none',
                  color: '#FFFFFF',
                  wordBreak: 'break-word',
                }}
                className="w-full xl:h-[40px] 2xl:h-[50px] flex items-center justify-start text-xs 2xl:text-sm mb-1 cursor-pointer rounded xl:rounded-md  px-2.5 2xl:px-3"
                key={index}
                onClick={() =>
                  setSelectedCategory({
                    id: item._id,
                    name: item.name,
                  })
                }
              >
                {item.name}
              </div>
            ))}
        </div>
      </div>
      <div>
        <div className="w-[78.5px] xl:w-[124px] 2xl:w-[155px]">
          <div
            style={{ background: '#12171D', color: '#CECECE' }}
            className="w-full h-[31px] xl:h-[33px] 2xl:h-[42px] flex items-center justify-center text-[11px] 2xl:text-xs mb-[6px] xl:mb-[9px] 2xl:mb-[11px] capitalize"
          >
            {selectedCategory.name} Channels ({visibleChannels.length})
          </div>
          <div
            ref={channelRef}
            onScroll={() => SyncScroll(channelRef, epgRef)}
            className="hidden-scrollbar-class h-[226px] xl:h-[255px] 2xl:h-[320px] "
          >
            <div ref={channelSliderHeightRef}>
              {!isEmpty(visibleChannels)
                ? visibleChannels.map((item, index) => (
                    <div
                      style={{
                        background: channelId === item._id ? '#263241' : '#12171D',
                        color: '#FFFFFF',
                        borderRadius: '6px 0px 0px 6px',
                        border: channelId === item._id ? '1px solid #707070' : 'none',
                      }}
                      className="w-full h-[36px] xl:h-[40px] 2xl:h-[50px] flex items-center justify-center mb-[2px] xl:mb-[3px] 2xl:mb-1 cursor-pointer relative"
                      key={index}
                      onClick={() => navigate(`/channel/${item._id}`)}
                    >
                      {item.is_restricted ? (
                        <p
                          style={{
                            background: '#414141',
                            color: '#FFE419',
                            padding: '0.5px',
                            borderRadius: '2px',
                            margin: '2px',
                          }}
                          className="text-[10px] absolute right-0 top-0"
                        >
                          Paid
                        </p>
                      ) : null}
                      <div className="flex" style={{ height: '75%', aspectRatio: '1.785' }}>
                        <CustomImage
                          imageUrl={getImage(
                            accountObj?.cf_domain_name,
                            item?.logo?.website?.s3_key,
                          )}
                          imgClass={'h-full w-full'}
                          loaderClass={'h-full w-full'}
                          imgAlt={item.name}
                        />
                      </div>
                    </div>
                  ))
                : [1, 2, 3, 4, 5, 6, 7].map((_, index) => (
                    <div
                      style={{
                        background: '#12171D',
                        borderRadius: '6px 0px 0px 6px',
                      }}
                      className="w-full h-[36px] xl:h-[40px] 2xl:h-[50px] mb-[2px] xl:mb-[3px] 2xl:mb-1 relative animate-pulse"
                      key={index}
                    >
                      <div className="flex" style={{ height: '75%', aspectRatio: '1.785' }}>
                        <div className="bg-gray-500 h-full w-full"></div>
                      </div>
                    </div>
                  ))}
            </div>
          </div>
        </div>
      </div>
      <div style={{}} className="ml-1 ">
        <div
          style={{ overflowX: 'auto', overflowY: 'visible' }}
          className="w-[calc(100vw-79px)] 2xl:w-[1342px] xl:w-[1068px] tv-epg-scrollbar-horizontal flex"
          ref={timeSlotRef}
          onScroll={() => SyncScroll(timeSlotRef, epgRef, true)}
        >
          <div
            style={{
              background: '#12171D',
              color: '#CECECE',
            }}
            className=" h-[31px] xl:h-[33px] 2xl:h-[42px] text-[11px] 2xl:text-xs relative      
          "
          >
            <div style={{ height: '49.5%' }} className="flex items-center"></div>
            <div style={{ height: '49.5%', background: '#000000' }} className="flex">
              {generateSlots().map((slot, index) => (
                <div
                  key={index}
                  style={{
                    width: timeStyleConfig[timeConfig].width,
                    fontSize: timeStyleConfig[timeConfig].timeFont,
                    borderRight: `1px solid ${
                      slot.time.includes('11:30 pm') ? '#FFBD29' : '#6F6F6F'
                    } `,
                  }}
                  className=" h-full pl-2 flex items-center font-semibold"
                >
                  <div
                    style={{ fontSize: timeStyleConfig[timeConfig].timeFont, color: '#FFBD29' }}
                    className=" font-semibold absolute top-0"
                  >
                    {!index ? (
                      <>
                        {slot.date} <span className="font-normal">(Today)</span>
                      </>
                    ) : null}
                    {slot.time.includes('12:00 am') ? <>{slot.date}</> : null}
                  </div>
                  <div className={index ? 'uppercase' : ''}>{index ? slot.time : 'Now'}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div
          style={{ overflowX: 'auto', overflowY: 'auto' }}
          ref={epgRef}
          onScroll={() => {
            SyncScroll(epgRef, channelRef)
            SyncScroll(epgRef, timeSlotRef, true)
          }}
          className="w-[calc(100vw-79px)] 2xl:w-[1342px] xl:w-[1068px] tv-epg-scrollbar-class tv-epg-scrollbar-horizontal-hidden  h-[226px] xl:h-[255px] 2xl:h-[320px] relative xl:mt-[3px] 2xl:mt-[5px]"
        >
          <div
            style={{
              width: `${2880 * timeStyleConfig[timeConfig].pixelPerMinute}px`,
              height: `max(${channelSliderHeight}px, 100%)`,
            }}
            className="absolute left-0 top-0 z-10 flex"
          >
            <div
              style={{
                border: '1.25px solid red',
                boxShadow: '0 0 5px rgba(255, 0, 0, 0.5)',
                transition: 'margin-left 0.5s ease-in-out',
                marginLeft: `${timerLeftMargin}px`,
              }}
              className="h-full z-10 absolute"
            ></div>
            {generateSlots().map((slot, index) => {
              return (
                <div
                  key={index}
                  style={{
                    width: timeStyleConfig[timeConfig].width,
                    borderRight: slot.time.includes('11:30 pm') ? `1px dashed #FFBD29` : 'none',
                  }}
                  className=" h-full flex items-center "
                ></div>
              )
            })}
          </div>
          {!isEmpty(channelEpgData) && !isEmpty(visibleChannels)
            ? visibleChannels.map((channelItem) => {
                const channelScheduleItem = channelEpgData.find(
                  (epg) => epg.channel_id === channelItem._id,
                ) || { schedule_data: [] }

                return (
                  <div
                    key={channelItem._id}
                    style={{
                      width: `${2880 * timeStyleConfig[timeConfig].pixelPerMinute}px`,
                      // border: '1px solid #727272',
                      background: '#11171F',
                    }}
                    className="flex relative mb-[2px] xl:mb-[3px] 2xl:mb-1 h-[36px] xl:h-[40px] 2xl:h-[50px] overflow-hidden rounded"
                  >
                    {!isEmpty(channelScheduleItem.schedule_data) ? (
                      channelScheduleItem.schedule_data.map((program, programIndex) => {
                        // Calculate program start and end times in channel's timezone
                        const programStart = new Date(`${program.start_time}Z`)
                        const programEnd = new Date(`${program.end_time}Z`)

                        // Calculate minutes from now to program start
                        const now = new Date()

                        const minutes = now.getMinutes()

                        // Check if time is between 5:00 and 5:30 (inclusive)
                        if (minutes > 0 && minutes <= 30) {
                          now.setMinutes(0, 0, 0, 0) // Sets time to 06:00:00.000
                        }
                        // Check if time is between 5:31 and 6:00 (inclusive)
                        else if (minutes > 30 || minutes === 0) {
                          now.setMinutes(30, 0, 0, 0)
                        }

                        let durationMs = 0

                        if (programStart > now) {
                          durationMs = Math.abs(programEnd.getTime() - programStart.getTime())
                        } else {
                          durationMs = Math.abs(programEnd.getTime() - now.getTime())
                        }

                        // 2. Calculate the difference in milliseconds
                        // Use Math.abs() to handle cases where the end date is in the past

                        // const minutesFromNowToStart = Math.max(0, programStart.diff(now, 'minutes'))
                        const minutesFromNowToStart = Math.floor(
                          (programStart.getTime() - now.getTime()) / 60000,
                        )

                        // Calculate program duration in minutes
                        const programDuration = Math.floor(durationMs / 60000)

                        return (
                          <div
                            key={programIndex}
                            style={{
                              left: `${
                                minutesFromNowToStart < 0
                                  ? 0
                                  : minutesFromNowToStart *
                                    timeStyleConfig[timeConfig].pixelPerMinute
                              }px`,
                              width: `${
                                programDuration * timeStyleConfig[timeConfig].pixelPerMinute
                              }px`,
                              fontSize: timeStyleConfig[timeConfig].fontSize,
                              // border: `1px solid ${getContentTileColor(program).borderColor}`,
                              background: getContentTileColor(program).bgColor,
                              color: '#FFFFFF',
                            }}
                            className="absolute h-full flex flex-col justify-center items-start px-2 overflow-hidden whitespace-nowrap text-ellipsis rounded xl:rounded-md"
                          >
                            <div>
                              {program.content_title}
                              <p style={{ color: '#868686' }} className="text-xs mt-[1px] 2xl:mt-1">
                                {`${convertEpgToLocalTime(
                                  program.start_time,
                                )} - ${convertEpgToLocalTime(program.end_time)}`}
                              </p>
                            </div>
                          </div>
                        )
                      })
                    ) : (
                      <>
                        {channelItem?.type === 'EXTERNAL' ? (
                          <div
                            style={{
                              left: `0`,
                              width: `100%`,
                              fontSize: timeStyleConfig[timeConfig].fontSize,
                              // border: `1px solid ${getContentTileColor(program).borderColor}`,
                              background: '#141446',
                              color: '#FFFFFF',
                            }}
                            className="absolute h-full flex flex-col justify-center items-start px-2 overflow-hidden whitespace-nowrap text-ellipsis rounded xl:rounded-md"
                          >
                            <p className="text-xs  ml-1 flex items-center px-2">
                              {` 24X7 LIVE ${
                                channelItem?.feed_type === 'AUDIO' ? 'Audio' : 'Video'
                              } Channel`}
                            </p>
                          </div>
                        ) : (
                          ''
                        )}
                      </>
                    )}
                  </div>
                )
              })
            : epgLoader
            ? [1, 2, 3, 4, 5, 6, 7].map((_, index) => (
                <div
                  style={{
                    background: '#12171D',
                    borderRadius: '6px 0px 0px 6px',
                  }}
                  className="w-full h-[36px] xl:h-[40px] 2xl:h-[50px] mb-[2px] xl:mb-[3px] 2xl:mb-1 relative animate-pulse"
                  key={index}
                >
                  <div className="flex" style={{ height: '75%', aspectRatio: '1.785' }}>
                    <div className="bg-gray-500 h-full w-full"></div>
                  </div>
                </div>
              ))
            : null}
        </div>
      </div>
    </div>
  )
}

export default IptvChannelEPG
